import React, { useEffect, useState } from 'react';
import { BillingRuleManager } from './BillingRuleManager';
import { ConsultantMonthlySummaryModal } from './ConsultantMonthlySummaryModal';
import { EventList } from './EventList';
import { S } from './MonthlyCalculationPanel.styles';
import { fmtDateRange, fmtMoney, fmtNum } from './monthlyCalculationUtils';

function Line({ label, value, tone }) {
  return (
    <div style={{ ...S.line, color: tone === 'minus' ? '#dc2626' : 'var(--text-sub)' }}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function latestRule(assignment) {
  return [...(assignment?.billing_rules || [])]
    .sort((a, b) => String(b.effective_from || '').localeCompare(String(a.effective_from || '')))[0] || null;
}

function ruleConditionText(rule) {
  if (!rule) return '계산 조건 없음';
  const salary = rule.salary_enabled ? `급여 ${fmtMoney(rule.monthly_salary)}` : '급여 제외';
  const source = rule.incentive_source || (rule.incentive_enabled === false ? 'none' : 'direct');
  const incentive = source === 'incentive_total'
    ? '인센티브 관리 금액'
    : source === 'none'
      ? '인센티브 제외'
      : `${fmtNum(rule.incentive_base_included_count || 0)}장 포함 · ${fmtMoney(rule.incentive_unit_price)}/장`;
  return `${salary} · ${incentive}`;
}

function segmentRuleText(segment) {
  return `조건 적용 ${fmtDateRange(segment.startDate, segment.endDate)} · ${ruleConditionText({
    salary_enabled: segment.salaryEnabled,
    monthly_salary: segment.monthlySalary,
    incentive_enabled: segment.incentiveEnabled,
    incentive_source: segment.incentiveSource,
    incentive_base_included_count: segment.incentiveBaseIncludedCount,
    incentive_unit_price: segment.incentiveUnitPrice,
  })}`;
}

function compactRuleText(row, assignment) {
  const segments = row?.billingSegments || [];
  if (segments.length === 1) return segmentRuleText(segments[0]);
  if (segments.length > 1) return `${segments.length}개 조건 적용 · ${segments.map(segmentRuleText).join(' / ')}`;

  const rule = latestRule(assignment);
  if (!rule) return '계산 조건 없음';
  const assignmentEnd = assignment?.end_date?.slice(0, 10) || null;
  const ruleEnd = rule.effective_to || assignmentEnd;
  return `${fmtDateRange(rule.effective_from || '-', ruleEnd)} · ${ruleConditionText(rule)}`;
}

function CompactRuleSummary({ row, assignment, open, onToggle }) {
  return (
    <div style={S.compactRule}>
      <div style={S.compactRuleMain}>
        <div style={S.compactRuleTitle}>이번 청구월 적용 조건</div>
        <div style={S.compactRuleText}>{compactRuleText(row, assignment)}</div>
      </div>
      <button type="button" style={S.btn} onClick={onToggle}>
        {open ? '조건 이력 접기' : '조건 이력 보기/수정'}
      </button>
    </div>
  );
}

function DetailLines({ rows, emptyText }) {
  if (!rows?.length) return <div style={S.muted}>{emptyText}</div>;
  return rows.map((row, index) => (
    <Line key={`${row.label}-${index}`} label={row.label} value={row.value} tone={row.tone} />
  ));
}

function reorderLines(summary) {
  const excluded = summary?.excluded || summary || {};
  const included = summary?.included || {};
  const rows = [];
  if (included.contractCount > 0) {
    const includedQty = included.weightedQuantity ?? included.quantity;
    rows.push({
      label: '재계약 포함',
      value: `${included.contractCount}건 · ${fmtNum(includedQty)}장`,
    });
  }
  if (excluded.contractCount > 0) {
    const excludedQty = excluded.weightedQuantity ?? excluded.quantity;
    rows.push({
      label: '재계약 제외',
      value: `${excluded.contractCount}건 · ${fmtNum(excludedQty)}장`,
      tone: 'minus',
    });
  }
  return rows;
}

export function MonthlyCalculationDetail({
  row,
  assignment,
  year,
  month,
  onRulesChanged,
  onDecisionChange,
  actionRequest = null,
  readOnlyRules = false,
  readOnlyDecisions = readOnlyRules,
  summaryScope = 'admin',
  summaryItemId = null,
  integratedMenuId = null,
}) {
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);

  useEffect(() => {
    if (!actionRequest || !row) return;
    if (Number(actionRequest.assignmentId) !== Number(row.assignmentId)) return;
    if (actionRequest.action === 'rules') setRulesOpen(true);
    if (actionRequest.action === 'summary') setSummaryOpen(true);
  }, [actionRequest, row]);

  function toggleRules() {
    setRulesOpen((prev) => !prev);
  }

  function openSummary() {
    setSummaryOpen(true);
  }

  if (!row) {
    return (
      <div style={S.detailPanel}>
        <div style={S.detailHeader}>
          <div style={S.selectedTitle}>선택된 1:1 직원이 없습니다.</div>
          <div style={S.detailSub}>왼쪽 목록에서 직원을 선택하면 계산 근거가 보입니다.</div>
        </div>
      </div>
    );
  }

  const productRows = (row.productBreakdown || []).map((item) => ({
    label: `${item.name} · 계약 ${fmtNum(item.contractQty)} / 취소 ${fmtNum(item.cancelledQty)}`,
    value: `${fmtNum(item.weightedQty)}장`,
  }));
  const leaveRows = (row.leaveDetails || []).map((item) => ({
    label: `${item.date}${item.dateTo ? `~${item.dateTo}` : ''} ${item.name}`,
    value: `${fmtNum(item.count)}일`,
  }));
  const reorderRows = reorderLines(row.reorderExcluded);
  const assignmentPeriod = fmtDateRange(row.assignmentStartDate, row.assignmentEndDate || '진행중');
  const activePeriod = fmtDateRange(row.activeStartDate, row.activeEndDate);

  return (
    <div style={S.detailPanel}>
      <div style={S.detailHeader}>
        <div style={S.detailHeaderTop}>
          <div style={S.detailHeaderInfo}>
            <div style={S.selectedTitle}>{row.consultantName}</div>
            <div style={S.detailSub}>
              {row.marketerName}의 1:1 직원
              <br />
              배정 기간: {assignmentPeriod} · 이번 청구월 반영: {activePeriod} ({row.activeDays}/{row.monthDays}일)
            </div>
          </div>
          <div style={S.detailActions}>
            <button type="button" style={S.detailActionBtn(rulesOpen)} onClick={toggleRules}>
              {rulesOpen ? '조건 닫기' : '조건 수정'}
            </button>
            <button type="button" style={S.detailActionBtn(false)} onClick={openSummary}>
              요약 보기
            </button>
          </div>
        </div>
        <div style={S.calcGrid}>
          <div style={S.metric}><div style={S.metricLabel}>청구액</div><div style={S.metricValue}>{fmtMoney(row.chargeAmount)}</div></div>
          <div style={S.metric}><div style={S.metricLabel}>기간 급여</div><div style={S.metricValue}>{fmtMoney(row.proratedSalary)}</div></div>
          <div style={S.metric}><div style={S.metricLabel}>연차 차감</div><div style={S.metricValue}>{fmtMoney(row.leaveDeductionAmount)}</div></div>
          <div style={S.metric}><div style={S.metricLabel}>인센티브</div><div style={S.metricValue}>{fmtMoney(row.incentiveAmount)}</div></div>
        </div>
        {(row.warnings || []).map((warning) => <span key={warning} style={S.warning}>{warning}</span>)}
      </div>

      <CompactRuleSummary row={row} assignment={assignment} open={rulesOpen} onToggle={toggleRules} />
      {rulesOpen && (
        <BillingRuleManager
          assignments={assignment ? [assignment] : []}
          integratedMenuId={integratedMenuId}
          onChanged={onRulesChanged}
          readOnly={readOnlyRules}
        />
      )}

      <div style={S.detailGrid}>
        <div style={S.detailBox}>
          <div style={S.detailTitle}>급여·연차 계산</div>
          <Line label="월급 기준" value={fmtMoney(row.monthlySalary)} />
          <Line label="일급 기준" value={fmtMoney(row.dailySalary)} />
          <Line label="연차 합계" value={`${fmtNum(row.leaveDays)}일`} />
          <Line label="무료 인정" value={`${fmtNum(row.leaveFreeDays)}일`} />
          <Line label="차감 대상" value={`${fmtNum(row.leaveDeductibleDays)}일`} />
        </div>

        <div style={S.detailBox}>
          <div style={S.detailTitle}>계약/취소 실적</div>
          <Line label="총 반영장수" value={`${fmtNum(row.weightedCount)}장`} />
          <DetailLines rows={productRows} emptyText="반영 실적 없음" />
          {reorderRows.length > 0 && <DetailLines rows={reorderRows} emptyText="" />}
        </div>

        <div style={{ ...S.detailBox, gridColumn: '1 / -1' }}>
          <div style={S.detailTitle}>청구 반영 계약</div>
          <EventList
            events={row.events || []}
            readOnly={readOnlyDecisions}
            onDecisionChange={!readOnlyDecisions && onDecisionChange
              ? (event, decisionStatus) => onDecisionChange({
                  assignmentId: row.assignmentId,
                  contractId: event.contractId,
                  eventType: event.type,
                  decisionStatus,
                })
              : null}
          />
        </div>

        <div style={S.detailBox}>
          <div style={S.detailTitle}>연차 내역</div>
          <DetailLines rows={leaveRows} emptyText="연차 없음" />
        </div>

      </div>

      {summaryOpen && (
        <ConsultantMonthlySummaryModal
          row={row}
          year={year}
          month={month}
          scope={summaryScope}
          chargeItemId={summaryItemId}
          integratedMenuId={integratedMenuId}
          onClose={() => setSummaryOpen(false)}
        />
      )}
    </div>
  );
}
